import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useAppStore } from '@/store';
import { DataSources } from '@/models/conversation';
import { updateConversation } from '@/api/conversations';

const ALL_SOURCES_ON: DataSources = {
  mail: true,
  sharepoint: true,
  teams_chats: true,
  teams_meetings: true,
  people: true,
};

const ALL_SOURCES_OFF: DataSources = {
  mail: false,
  sharepoint: false,
  teams_chats: false,
  teams_meetings: false,
  people: false,
};

/** All sources shown in the info popover */
const WORK_SOURCES: { label: string; available: boolean }[] = [
  { label: 'Outlook emails', available: true },
  { label: 'Calendar & meeting transcripts', available: true },
  { label: 'Teams chats', available: true },
  { label: 'SharePoint documents', available: true },
  { label: 'People directory', available: true },
  { label: 'CRM', available: false },
  { label: 'D365 FO', available: false },
];

interface SourceTogglesProps {
  disabled?: boolean;
  compact?: boolean;
}

export const SourceToggles: React.FC<SourceTogglesProps> = ({ disabled = false, compact = false }) => {
  const theme = useTheme();
  const [infoAnchor, setInfoAnchor] = useState<null | HTMLElement>(null);
  const dataSources = useAppStore((s) => s.dataSources);
  const setDataSources = useAppStore((s) => s.setDataSources);
  const selectedConversation = useAppStore((s) => s.selectedConversation);

  const isWorkActive = Object.values(dataSources).some(Boolean);

  /** Toggle all sources ON/OFF and persist to conversation (fire-and-forget) */
  const handleWorkToggle = () => {
    const updated = isWorkActive ? ALL_SOURCES_OFF : ALL_SOURCES_ON;
    setDataSources(updated);
    if (selectedConversation?.id) {
      updateConversation(selectedConversation.id, { data_sources: updated } as any).catch(
        (err) => console.error('Failed to persist data sources:', err)
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', ml: compact ? 0.5 : 2 }}>
      <Button
        onClick={handleWorkToggle}
        disabled={disabled}
        variant="outlined"
        size="small"
        sx={{
          borderRadius: '20px',
          textTransform: 'none',
          fontWeight: 600,
          px: 1.5,
          py: 0.4,
          fontSize: '0.8125rem',
          minWidth: 'unset',
          lineHeight: 1.5,
          borderColor: isWorkActive
            ? 'primary.main'
            : theme.palette.mode === 'light' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
          color: isWorkActive ? 'primary.main' : 'text.secondary',
          bgcolor: isWorkActive
            ? theme.palette.mode === 'light' ? 'rgba(0, 122, 195, 0.1)' : 'rgba(0, 122, 195, 0.18)'
            : 'transparent',
          '&:hover': {
            borderColor: 'primary.main',
            color: 'primary.main',
            bgcolor: isWorkActive
              ? theme.palette.mode === 'light' ? 'rgba(0, 122, 195, 0.18)' : 'rgba(0, 122, 195, 0.28)'
              : theme.palette.mode === 'light' ? 'rgba(0, 122, 195, 0.06)' : 'rgba(0, 122, 195, 0.1)',
          },
          '&.Mui-disabled': { opacity: 0.45 },
        }}
      >
        Work
      </Button>

      <IconButton
        onClick={(e) => setInfoAnchor(e.currentTarget)}
        size="small"
        sx={{
          color: 'text.disabled',
          p: 0.25,
          '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
        }}
        aria-label="Work sources info"
        disableRipple
      >
        <InfoIcon sx={{ fontSize: 15 }} />
      </IconButton>

      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.28))',
            mt: -1.5,
            borderRadius: '12px',
            minWidth: 230,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateX(-50%) translateY(50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1.75 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
            Work sources
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 1.5, lineHeight: 1.45 }}
          >
            When enabled, Bolt can search your company data to answer questions.
          </Typography>
          {WORK_SOURCES.map(({ label, available }) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.35,
                opacity: available ? 1 : 0.4,
              }}
            >
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor: available ? 'primary.main' : 'text.disabled',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: available ? 'text.primary' : 'text.disabled', fontSize: '0.8125rem' }}
              >
                {label}
                {!available && (
                  <Box component="span" sx={{ fontSize: '0.7rem', ml: 0.5, color: 'text.disabled' }}>
                    (coming soon)
                  </Box>
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default SourceToggles;
